/**
 * Yotpo utility functions for handling review requests
 * Provides robust error handling, retry logic, and request deduplication
 */

// Cache for request deduplication
const yotpoRequestCache = new Map();

/**
 * Send a Yotpo request with robust error handling, retry logic, and deduplication
 * @param {string} prodId - Product ID
 * @param {string} prodUrl - Product URL
 * @param {number} retryCount - Current retry attempt (internal use)
 * @returns {Promise<Object|null>} Yotpo widget data or null on failure
 */
const sendYotpoRequest = async (prodId, prodUrl, retryCount = 0) => {
    // Check if request is already in progress
    const cacheKey = `${prodId}_${prodUrl}`;
    if (yotpoRequestCache.has(cacheKey)) {
        return yotpoRequestCache.get(cacheKey);
    }

    // Validate required parameters
    if (!prodId || !prodUrl) {
        console.warn('sendYotpoRequest: Missing required parameters', { prodId, prodUrl });
        return null;
    }

    // Check if yotpo object exists
    if (!window.yotpo || !window.yotpo.appKey) {
        console.warn('sendYotpoRequest: Yotpo not initialized');
        return null;
    }

    const yotpoKey = window.yotpo.appKey;
    const yotpoVersion = window.yotpo.userSettings?.version || '1.0';
    
    const data = {
        methods: [
            {
                method: "bottomline",
                params: {
                    pid: prodId,
                    link: prodUrl,
                    skip_average_score: true,
                    widget_product_id: prodId 
                }
            }
        ],
        app_key: yotpoKey,
        is_mobile: false,
        widget_version: yotpoVersion
    };
    
    const yotpoUrl = `https://staticw2.yotpo.com/batch/app_key/${yotpoKey}/domain_key/${prodId}/widget/bottomline`;
    
    // Create a promise and cache it
    const requestPromise = (async () => {
        try {
            // Create AbortController for timeout
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
            
            const response = await fetch(yotpoUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(data),
                signal: controller.signal
            });
            
            clearTimeout(timeoutId);
            
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }
            
            const result = await response.json();
            
            // Remove from cache after successful completion
            yotpoRequestCache.delete(cacheKey);
            
            return result;
        } catch (error) {
            // Remove from cache on error
            yotpoRequestCache.delete(cacheKey);
            
            if (error.name === 'AbortError') {
                console.warn(`sendYotpoRequest: Request timeout for product ${prodId}`);
            } else {
                console.error(`sendYotpoRequest: Request failed for product ${prodId}:`, error);
            }
            
            // Retry logic (max 2 retries)
            if (retryCount < 2) {
                console.log(`sendYotpoRequest: Retrying request for product ${prodId} (attempt ${retryCount + 1})`);
                await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1))); // Exponential backoff
                return sendYotpoRequest(prodId, prodUrl, retryCount + 1);
            }
            
            return null;
        }
    })();
    
    // Cache the promise
    yotpoRequestCache.set(cacheKey, requestPromise);
    
    return requestPromise;
};

/**
 * Process Yotpo reviews for multiple product cards with improved error handling
 * @param {NodeList} productCards - Collection of product card elements
 * @returns {Promise<void>}
 */
const processYotpoReviews = async (productCards) => {
    const reviewPromises = [];
    
    for (const productCard of productCards) {
        const productBottomLine = productCard.querySelector('.product-card-info .productInfoRight .plpRatings .yotpo.bottomLine');
        const prodUrl = productCard.querySelector('.product-featured-image-link')?.href;
        
        if (productBottomLine && prodUrl) {
            const prodId = productBottomLine.getAttribute('data-product-id');
            
            if (prodId) {
                // Add request to promises array for parallel processing
                reviewPromises.push(
                    sendYotpoRequest(prodId, prodUrl)
                        .then(yotpoWidget => {
                            if (yotpoWidget && yotpoWidget[0] && yotpoWidget[0].result) {
                                const yotpoReviews = yotpoWidget[0].result;
                                productBottomLine.innerHTML = yotpoReviews;
                                return { success: true, prodId };
                            } else {
                                console.warn(`sendYotpoRequest: No valid data returned for product ${prodId}`);
                                return { success: false, prodId, error: 'No valid data' };
                            }
                        })
                        .catch(error => {
                            console.warn(`sendYotpoRequest: Failed to load reviews for product ${prodId}:`, error);
                            return { success: false, prodId, error: error.message };
                        })
                );
            } else {
                console.warn('sendYotpoRequest: No product ID found for element');
            }
        }
    }
    
    // Wait for all requests to complete
    try {
        const results = await Promise.allSettled(reviewPromises);
        const successful = results.filter(r => r.status === 'fulfilled' && r.value.success).length;
        const failed = results.filter(r => r.status === 'rejected' || (r.status === 'fulfilled' && !r.value.success)).length;
        
        console.log(`Yotpo reviews: ${successful} successful, ${failed} failed`);
    } catch (error) {
        console.error('Error processing Yotpo reviews:', error);
    }
};

// Export functions for use in other files
if (typeof module !== 'undefined' && module.exports) {
    // Node.js environment
    module.exports = { sendYotpoRequest, processYotpoReviews };
} else {
    // Browser environment
    window.YotpoUtils = { sendYotpoRequest, processYotpoReviews };
}
